import type { SelectOption } from "@SeemplicityDev/shared";
import {
  INTEGRATION_CATEGORY,
  type AgentDefinition,
  type AnalystInstanceBase,
  type AvailableIntegration,
  type IntegrationCategory,
  type IntegrationInstance,
  type IntegrationTypeValue,
} from "../../../AnalystRuns/types";

export interface AnalystConfigFormData {
  agent_type: string | null;
  name: string;
  ticketProvider: SelectOption | null;
  datasources: SelectOption[];
}

interface IntegrationOptionSource {
  id: number;
  integration_type: string;
  display_name: string;
  integration_category?: IntegrationCategory | null;
}

export const integrationSignature = (
  integrations: { type: string; display_name: string }[]
): string =>
  integrations
    .map(({ type, display_name }) => `${type}::${display_name}`)
    .sort()
    .join("|");

export const normalizeName = (name: string): string =>
  name.trim().toLowerCase();

export const buildIntegrationLabel = ({
  display_name,
  integration_type,
  integration_category,
}: IntegrationOptionSource): string => {
  const isDatasource =
    integration_category === INTEGRATION_CATEGORY.DATASOURCE &&
    !!integration_type;
  return isDatasource
    ? `${display_name.trim()} - ${integration_type}`
    : display_name;
};

export const toIntegrationOption = (
  integration: IntegrationOptionSource
): SelectOption => ({
  label: buildIntegrationLabel(integration),
  value: integration.id,
  integration_type: integration.integration_type,
  display_name: integration.display_name,
});

export const toAgentTypeSelectOptions = (
  definitions: AgentDefinition[]
): SelectOption[] =>
  definitions.map(({ agent_type, display_name }) => ({
    label: display_name,
    value: agent_type.toLowerCase(),
  }));

export const findAgentTypeOption = (
  options: SelectOption[],
  value: string | null
): SelectOption | null =>
  options.find((option) => option.value === value) ??
  (value ? { label: `${value} (unknown type)`, value } : null);

export const toIntegrationSelectOptions = (
  integrations: AvailableIntegration[]
): SelectOption[] => integrations.map(toIntegrationOption);

export const optionToInstance = (opt: SelectOption): IntegrationInstance => ({
  id: opt.value as number,
  integration_type: opt.integration_type as IntegrationTypeValue,
  display_name: opt.display_name as string,
});

export const buildDefaultValues = (
  initialConfig?: AnalystInstanceBase
): AnalystConfigFormData => {
  if (!initialConfig) {
    return {
      agent_type: null,
      name: "",
      ticketProvider: null,
      datasources: [],
    };
  }
  let ticketProvider: SelectOption | null = null;
  const datasources: SelectOption[] = [];
  initialConfig.integrations.forEach((integration) => {
    if (integration.integration_category === INTEGRATION_CATEGORY.TICKET) {
      ticketProvider = ticketProvider ?? toIntegrationOption(integration);
    } else if (
      integration.integration_category === INTEGRATION_CATEGORY.DATASOURCE
    ) {
      datasources.push(toIntegrationOption(integration));
    }
  });
  return {
    agent_type: initialConfig.agent_type,
    name: initialConfig.display_name,
    ticketProvider,
    datasources,
  };
};
